import { RestApiPaymentMethodDto, RestApiTipDto, RestApiTipPresetDto } from '@order-mage/integration-sdk';
import type { CartTipSelection, CartTipType } from '@/entities/cart/model/cart-context';
import { Currency } from '@/shared/model/Currency';

/**
 * Tips arrive nested inside their payment method, and nothing upstream prunes them — the
 * eshop filters `public` itself for the method (see `payment-method-import.processor`),
 * so it must do the same for the tips it carries. A draft or soft-deleted tip must never
 * be offered, let alone charged.
 */
export const isTipPublished = (tip: Pick<RestApiTipDto, 'public' | 'deletedAt'>): boolean =>
    tip.public && !tip.deletedAt;

/**
 * A tip reaches the eshop only through the payment method it is connected to, and may
 * additionally restrict itself to a set of delivery methods:
 * `null`/absent = offer everywhere, `[]` = never offer, `[ids]` = only those methods.
 */
export const isTipAvailableForDeliveryMethod = (
    tip: Pick<RestApiTipDto, 'restrictDeliveryMethodIds'>,
    deliveryMethodId: string | null | undefined,
): boolean => {
    const restriction = tip.restrictDeliveryMethodIds;
    if (restriction === null || restriction === undefined) return true;
    if (!deliveryMethodId) return false;
    return restriction.includes(deliveryMethodId);
};

/**
 * Tips offered for the current payment + delivery method combination, in BE sort order.
 * Single source for both the tip box and the pricing math — they must never disagree
 * about which tips count.
 */
export const getApplicableTips = (
    paymentMethod: Pick<RestApiPaymentMethodDto, 'tips'> | null | undefined,
    deliveryMethodId: string | null | undefined,
): RestApiTipDto[] =>
    (paymentMethod?.tips ?? [])
        .filter((tip) => isTipPublished(tip) && isTipAvailableForDeliveryMethod(tip, deliveryMethodId))
        .sort((a, b) => a.sortIndex - b.sortIndex);

export type CartTipPreset = {
    /** `percentage` presets live in the currency-less group; `fixed` ones in a per-currency group. */
    type: Extract<CartTipType, 'percentage' | 'fixed'>;
    value: RestApiTipPresetDto['value'];
    label: RestApiTipPresetDto['label'];
    icons: RestApiTipPresetDto['icons'];
};

/**
 * Flattens a tip's preset groups into the list to render: the percent presets plus the
 * fixed presets priced in the selected currency (fixed groups for other currencies are
 * not applicable to this cart).
 */
export const getTipPresets = (
    tip: Pick<RestApiTipDto, 'presetGroups'>,
    selectedCurrency: Currency,
): CartTipPreset[] =>
    (tip.presetGroups ?? []).flatMap((group) => {
        if (group.currency && (group.currency as string) !== selectedCurrency) return [];
        const type = group.currency ? 'fixed' : 'percentage';
        return (group.presets ?? []).map((preset) => ({
            type: type as CartTipPreset['type'],
            value: preset.value,
            label: preset.label,
            icons: preset.icons,
        }));
    });

/**
 * Raw (unrounded) money value of a tip selection. Percentages are resolved against the
 * subtotal; fixed presets and custom amounts are taken as-is. Callers must still put the
 * result through `roundTip` — a fractional tip breaks the POS (see PRICE rounding).
 */
export const resolveTipAmount = (selection: CartTipSelection | undefined, subtotal: number): number => {
    if (!selection?.enabled) return 0;
    const value = parseFloat(selection.value);
    if (!Number.isFinite(value) || value < 0) return 0;
    return selection.type === 'percentage' ? (subtotal * value) / 100 : value;
};
